import { ANGLE_STEPS, cosA, iatan2, idiv, imax, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { cellAtWorld, cellCenter, findPath, heroWalkable } from '../grid.ts';
import type { BattleState, Enemy, GolemRules, GolemState, GolemUnit, Hero, SendaNpc } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';

/**
 * The workshop's golems (docs/sendas/artifice.md, chapter 1, acts 2 and 3; «Gólem de taller»). Nadd's old golems stand
 * at their posts, asleep, a red lamp on the chest sweeping in front of them like a lantern (`lamp`). The hero walking
 * into its light wakes one (`golemWake`); awake it is slow, turns slowly (`turn` angle steps a tick) and goes for the
 * nearest (the hero or the path's person); in reach it raises its arm (`warn`, its aim held: a three-second warning)
 * and brings it down in an arc in front of it (`heroBp` of the hero's life, `blows` of a person's). Its boiler is in
 * its back, behind a hinged lid: a blow from within `back` of its back opens it and the golem goes out `off` (its lamp
 * dark, still), then lights again; a blow from anywhere else lands at `frontBp` (the iron of its chest). Asleep it is
 * iron: nothing aims at it.
 *
 * The Overseer (`boss`, act 3): the biggest, two boilers. A boiler opens after `capBp` of its life struck from behind and stays
 * open `open`; the Overseer slows with one open, and with both open at once it goes out for good (`bossOut`). Struck
 * from behind it swings round on the one who did it and raises its arm at them: each blow on its back is a dance.
 * Every golem spins its arms out (`spin`) when the hero comes within its reach, its back too: standing behind it is not
 * safe for long. The Overseer stamps (`stamp`): the whole hall shakes, nowhere out of it, so its fight can't be long.
 * In the lift's hall they come for the hero (the train's noise is its), a person only when much nearer.
 *
 * The golems are nightmares of the fight (ogres in the sim, drawn as golems: the render reads `s.senda.golem`); this
 * module moves them (`golemHold`, from the enemies' tick) and reads every blow on them (`golemTakes`, from combat).
 *
 * Cost: a handful of golems; each looks at the hero and the path's people once a tick (no nightmare against another);
 * a way round a wall is looked for at most once a second, and only when the straight line is blocked.
 */

/** The shortest turn from `a` to `b` (angle steps, −512 to 511). */
export const turnOf = (a: number, b: number): number => ((((b - a) % ANGLE_STEPS) + ANGLE_STEPS + ANGLE_STEPS / 2) % ANGLE_STEPS) - ANGLE_STEPS / 2;

const norm = (a: number): number => ((a % ANGLE_STEPS) + ANGLE_STEPS) % ANGLE_STEPS;

/** The golem of an enemy (undefined: not one). */
export const golemOf = (s: BattleState, id: number): GolemUnit | undefined => {
  const list = s.senda?.golem?.list;
  if (!list) return undefined;
  for (const g of list) if (g.id === id) return g;
  return undefined;
};

/** Where an asleep golem's lamp points now (its post's facing, sweeping). */
export const lampFace = (s: BattleState, r: GolemRules, g: GolemUnit): number => {
  const P = r.lamp.period;
  const t = (s.tick + g.phase) % P;
  return norm(g.face + idiv(r.lamp.sweep * sinA(idiv(t * ANGLE_STEPS, P)), 10000));
};

/** Whether a point is in a cone from (x, y) facing `face`, `range` long and `arc` either side. */
export const inArc = (x: number, y: number, face: number, range: number, arc: number, px: number, py: number): boolean => {
  if (!withinRange(x, y, px, py, range)) return false;
  if (px === x && py === y) return true;
  const d = turnOf(face, iatan2(py - y, px - x));
  return d <= arc && d >= -arc;
};

/** A golem at a post, asleep (the hall's `hall`; −1 the lift's hall). */
export const golemPost = (s: BattleState, r: GolemRules, p: { x: number; y: number; face: number }, hall: number, boss = false): GolemUnit => {
  const st = (s.senda as NonNullable<BattleState['senda']>).golem as GolemState;
  const hp = boss && r.boss ? r.boss.hp : r.hp;
  const e = spawnEnemy(s, 'ogre', 1, false, 0, p.x, p.y, hp);
  e.facing = p.face;
  e.anim = 'idle';
  const g: GolemUnit = { id: e.id, x: p.x, y: p.y, face: p.face, hall, mode: 0, t: 0, wind: -1, aim: p.face, next: 0, phase: idiv(st.list.length * r.lamp.period * 3, 7), boss, hits: boss ? [0, 0] : [], open: boss ? [0, 0] : [], step: -1, stepAt: -100, spin: -1, spinNext: 0, stamp: -1, stampNext: 0 };
  st.list.push(g);
  return g;
};

/** Wakes a golem (its lamp turns full on the one who woke it). */
export const golemWake = (s: BattleState, g: GolemUnit, cue?: (c: string) => void): void => {
  if (g.mode !== 0) return;
  g.mode = 1;
  g.next = s.tick + 6;
  const r = s.rules?.senda?.golem;
  if (r) {
    g.spinNext = s.tick + r.spin.every;
    g.stampNext = s.tick + idiv(r.stamp.every, 2);
  }
  const e = s.enemies.find((x) => x.id === g.id);
  ev(s, { t: 'senda', kind: 'golemWake', npc: '', x: g.x, y: g.y, n: g.id });
  if (e) e.anim = 'idle';
  cue?.(g.boss ? 'bossWake' : 'wake');
};

/** Asleep: iron at its post, nothing aims at it. */
export const golemAsleep = (s: BattleState, e: Enemy): boolean => {
  const g = golemOf(s, e.id);
  return !!g && g.mode === 0;
};

/**
 * The golems' tick (from the act's): an asleep golem's lamp sweeps and wakes it on the hero; an open boiler closes and
 * the golem lights again; the Overseer's boilers close one by one; a broken golem is marked so.
 */
export const golemTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.golem;
  const st = s.senda?.golem;
  if (!r || !st) return;
  // What the blows on them did since (combat can't say the fight's lines itself).
  if (st.pend.length) {
    for (const c of st.pend) cue(c);
    st.pend.length = 0;
  }
  const h = s.heroes.find((x) => x.downTicks === 0);
  for (const g of st.list) {
    if (g.mode === 3) continue;
    const e = s.enemies.find((x) => x.id === g.id);
    if (!e || e.hp <= 0) {
      g.mode = 3;
      ev(s, { t: 'senda', kind: 'golemDown', npc: '', x: e?.x ?? g.x, y: e?.y ?? g.y, n: g.id });
      continue;
    }
    if (g.mode === 0) {
      e.facing = lampFace(s, r, g);
      if (h && inArc(e.x, e.y, e.facing, r.lamp.range, r.lamp.arc, h.x, h.y)) golemWake(s, g, cue);
      continue;
    }
    if (g.mode === 2 && s.tick >= g.t) {
      g.mode = 1;
      g.next = s.tick + 12;
      ev(s, { t: 'senda', kind: 'relit', npc: '', x: e.x, y: e.y, n: g.id });
    }
    if (g.boss) for (let k = 0; k < g.open.length; k++) if ((g.open[k] as number) > 0 && s.tick >= (g.open[k] as number)) g.open[k] = 0;
    // The Overseer's stamp: its arms up, then the whole floor (nowhere in the hall is out of it).
    if (g.boss && g.mode === 1) {
      if (g.stamp < 0 && s.tick >= g.stampNext) {
        g.stamp = s.tick + r.stamp.warn;
        ev(s, { t: 'senda', kind: 'stampWarn', npc: '', x: e.x, y: e.y });
        cue('stamp');
      } else if (g.stamp >= 0 && s.tick >= g.stamp) {
        g.stamp = -1;
        g.stampNext = s.tick + r.stamp.every;
        ev(s, { t: 'senda', kind: 'stamp', npc: '', x: e.x, y: e.y });
        for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, r.stamp.heroBp), e, 'physical', true);
        for (const n of s.senda?.npcs ?? []) if (n.hp > 0 && !n.away) n.hp = imax(0, n.hp - r.stamp.blows);
      }
    }
  }
};

/** The one a golem goes for: the nearest of the standing hero and the path's people who can be struck. */
const quarry = (s: BattleState, e: Enemy): { x: number; y: number; hero: Hero | null; npc: SendaNpc | null } | null => {
  let best: { x: number; y: number; hero: Hero | null; npc: SendaNpc | null } | null = null;
  let bd = 0x7fffffff;
  for (const h of s.heroes) {
    if (h.downTicks > 0) continue;
    const d = Math.abs(h.x - e.x) + Math.abs(h.y - e.y);
    if (d < bd) {
      bd = d;
      best = { x: h.x, y: h.y, hero: h, npc: null };
    }
  }
  // (In the lift's hall the train's noise is the hero's: they come for whoever works it; a person only when much nearer.)
  const lean = s.rules?.senda?.gears ? 2500 : 0;
  for (const n of s.senda?.npcs ?? []) {
    if (n.hp <= 0 || n.away) continue;
    const d = Math.abs(n.x - e.x) + Math.abs(n.y - e.y) + lean;
    if (d < bd) {
      bd = d;
      best = { x: n.x, y: n.y, hero: null, npc: n };
    }
  }
  return best;
};

/** Turns `e` toward an angle by at most `turn` a tick. */
const turnToward = (e: Enemy, to: number, turn: number): void => {
  const d = turnOf(e.facing, to);
  e.facing = norm(e.facing + (d > turn ? turn : d < -turn ? -turn : d));
};

/** Where to walk toward (x, y): straight if the next step is floor, else the first cell of a way round (once a second). */
const stepTo = (s: BattleState, g: GolemUnit, e: Enemy, x: number, y: number): { x: number; y: number } => {
  const dx = x - e.x;
  const dy = y - e.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  const ahead = cellAtWorld(s, e.x + idiv(dx * 700, d), e.y + idiv(dy * 700, d));
  if (heroWalkable(s, ahead)) return { x, y };
  if (s.tick - g.stepAt >= 12 || g.step < 0) {
    g.stepAt = s.tick;
    const way = findPath(s, cellAtWorld(s, e.x, e.y), cellAtWorld(s, x, y));
    g.step = way[0] ?? -1;
  }
  return g.step >= 0 ? cellCenter(s.map.radius, g.step) : { x, y };
};

/** The golem's blow: what is in its arc in front of it, `reach` long. */
const slam = (s: BattleState, r: GolemRules, g: GolemUnit, e: Enemy): void => {
  const B = g.boss && r.boss ? r.boss : null;
  const reach = B ? B.reach : r.reach;
  const arc = B ? B.arc : r.arc;
  const heroBp = B ? B.heroBp : r.heroBp;
  const blows = B ? B.blows : r.blows;
  ev(s, { t: 'senda', kind: 'golemHit', npc: '', x: e.x + idiv(cosA(g.aim) * 800, 10000), y: e.y + idiv(sinA(g.aim) * 800, 10000), n: g.aim });
  e.anim = 'attack';
  e.animT = 0;
  for (const h of s.heroes) {
    if (h.downTicks > 0 || !inArc(e.x, e.y, g.aim, reach, arc, h.x, h.y)) continue;
    damageHero(s, h, mulBp(h.stats.maxHp, heroBp), e, 'physical', true);
  }
  for (const n of s.senda?.npcs ?? []) {
    if (n.hp <= 0 || n.away || !inArc(e.x, e.y, g.aim, reach, arc, n.x, n.y)) continue;
    n.hp -= blows;
    n.flinch = 12;
    ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
    if (n.hp < 0) n.hp = 0;
  }
};

/** A golem's move (from the enemies' tick; true: it's a golem, nothing else moves it). */
export const golemHold = (s: BattleState, e: Enemy): boolean => {
  const r = s.rules?.senda?.golem;
  const g = r ? golemOf(s, e.id) : undefined;
  if (!r || !g) return false;
  if (g.mode === 0 || g.mode === 3) {
    e.anim = 'idle';
    return true;
  }
  if (g.mode === 2) {
    // Out: slumped where it stands, its arms down.
    e.anim = 'idle';
    g.wind = -1;
    g.spin = -1;
    return true;
  }
  // The sweep: its arms out, spinning; at the end, all round it within `r`.
  if (g.spin >= 0) {
    e.anim = 'cast';
    if (s.tick >= g.spin) {
      g.spin = -1;
      g.spinNext = s.tick + r.spin.every;
      g.next = s.tick + r.rest;
      e.anim = 'attack';
      e.animT = 0;
      ev(s, { t: 'senda', kind: 'golemSpin', npc: '', x: e.x, y: e.y, n: 0 });
      for (const h of s.heroes) if (h.downTicks === 0 && withinRange(e.x, e.y, h.x, h.y, r.spin.r)) damageHero(s, h, mulBp(h.stats.maxHp, r.spin.heroBp), e, 'physical', true);
      for (const n of s.senda?.npcs ?? []) {
        if (n.hp <= 0 || n.away || !withinRange(e.x, e.y, n.x, n.y, r.spin.r)) continue;
        n.hp = imax(0, n.hp - r.spin.blows);
        ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
      }
    }
    return true;
  }
  if (g.wind < 0 && s.tick >= g.spinNext && s.heroes.some((h) => h.downTicks === 0 && withinRange(e.x, e.y, h.x, h.y, r.spin.r - 200))) {
    g.spin = s.tick + r.spin.warn;
    e.anim = 'cast';
    e.animT = 0;
    ev(s, { t: 'senda', kind: 'golemSpin', npc: '', x: e.x, y: e.y, n: 1 });
    return true;
  }
  const B = g.boss && r.boss ? r.boss : null;
  const turn = B ? B.turn : r.turn;
  // (The Overseer with a boiler open drags itself at half pace.)
  const pace = B ? (g.open.some((o) => o > 0) ? idiv(B.pace, 2) : B.pace) : r.pace;
  const reach = B ? B.reach : r.reach;
  const warn = B ? B.warn : r.warn;
  if (g.wind >= 0) {
    // The arm up: its aim held (it turns no more), the blow at the end.
    e.facing = g.aim;
    e.anim = 'cast';
    if (s.tick >= g.wind) {
      slam(s, r, g, e);
      g.wind = -1;
      g.next = s.tick + r.rest;
    }
    return true;
  }
  const q = quarry(s, e);
  if (!q) {
    e.anim = 'idle';
    return true;
  }
  const to = iatan2(q.y - e.y, q.x - e.x);
  turnToward(e, to, turn);
  const off = turnOf(e.facing, to);
  if (withinRange(e.x, e.y, q.x, q.y, reach - 150)) {
    // In reach: the arm goes up once it faces near enough (its blow lands where it faces then).
    e.anim = 'idle';
    if (s.tick >= g.next && off <= 96 && off >= -96) {
      g.wind = s.tick + warn;
      g.aim = e.facing;
      e.anim = 'cast';
      e.animT = 0;
      ev(s, { t: 'senda', kind: 'golemWarn', npc: '', x: e.x, y: e.y, n: g.aim });
    }
    return true;
  }
  // Walking: only forward, and only facing near the way (it turns in place first).
  if (off > 160 || off < -160) {
    e.anim = 'idle';
    return true;
  }
  const p = stepTo(s, g, e, q.x, q.y);
  const dx = p.x - e.x;
  const dy = p.y - e.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  const nx = e.x + idiv(dx * pace, d);
  const ny = e.y + idiv(dy * pace, d);
  if (heroWalkable(s, cellAtWorld(s, nx, ny))) {
    e.x = nx;
    e.y = ny;
  }
  e.anim = 'walk';
  return true;
};

/**
 * A blow on a golem (combat's damage): from behind, its boiler (the small ones go out; the Overseer's lid takes the
 * blow toward opening); from anywhere else at `frontBp`, unless it is out (its boiler open: whole).
 */
export const golemTakes = (s: BattleState, e: Enemy, dmg: number, fromX: number, fromY: number): number => {
  const cue = (c: string): void => void ((s.senda as NonNullable<BattleState['senda']>).golem as GolemState).pend.push(c);
  const r = s.rules?.senda?.golem;
  const g = r ? golemOf(s, e.id) : undefined;
  if (!r || !g || g.mode === 3) return dmg;
  if (g.mode === 0) golemWake(s, g, cue);
  const from = iatan2(fromY - e.y, fromX - e.x);
  const d = turnOf(norm(e.facing + ANGLE_STEPS / 2), from);
  const behind = (fromX !== e.x || fromY !== e.y) && d <= r.back && d >= -r.back;
  if (g.boss && r.boss) {
    if (behind && g.mode === 1) {
      // It swings round on whoever struck it there and raises its arm at them (three seconds to get round it again).
      e.facing = from;
      if (g.wind < 0) {
        g.wind = s.tick + r.boss.warn;
        g.aim = from;
        ev(s, { t: 'senda', kind: 'golemWarn', npc: '', x: e.x, y: e.y, n: from });
      }
    }
    if (behind) {
      // The lid of the next shut boiler takes it; enough of them and it opens.
      const k = (g.open[0] as number) > 0 ? 1 : 0;
      if ((g.open[k] as number) === 0) {
        g.hits[k] = (g.hits[k] as number) + dmg;
        if ((g.hits[k] as number) >= mulBp(e.maxHp, r.boss.capBp)) {
          g.hits[k] = 0;
          g.open[k] = s.tick + r.boss.open;
          ev(s, { t: 'senda', kind: 'boiler', npc: '', x: e.x, y: e.y, n: k });
          cue(`boiler${k + 1}`);
          if (g.open.every((o) => o > 0)) {
            g.mode = 2;
            g.t = 0x7fffffff;
            ev(s, { t: 'senda', kind: 'bossOut', npc: '', x: e.x, y: e.y, n: g.id });
            cue('bossOut');
          }
        }
      }
      return dmg;
    }
    return g.open.some((o) => o > 0) ? dmg : mulBp(dmg, r.frontBp);
  }
  if (g.mode === 2) return dmg;
  if (behind) {
    g.mode = 2;
    g.t = s.tick + r.off;
    g.wind = -1;
    // (Out, its arms drop: a spin it had begun is gone.)
    g.spin = -1;
    ev(s, { t: 'senda', kind: 'boiler', npc: '', x: e.x, y: e.y, n: g.id });
    cue('boiler');
    return dmg;
  }
  if (s.tick % 6 === 0) ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'blocked' });
  return mulBp(dmg, r.frontBp);
};

/** Golems that stand awake (or out, about to light again): their count, of a hall's or all. */
export const golemsUp = (s: BattleState, hall?: number): number => {
  let n = 0;
  for (const g of s.senda?.golem?.list ?? []) if ((g.mode === 1 || g.mode === 2) && (hall === undefined || g.hall === hall) && !g.boss) n++;
  return n;
};
